import { system } from '@/lib/dto/system/system';
import Head from 'next/head';

interface ServicePageMetaProps {
  title: string;
  description: string;
  keywords: string[];
  serviceType: string;
  url: string;
  image?: string;
}

export default function ServicePageMeta({
  title,
  description,
  keywords,
  serviceType,
  url,
  image
}: ServicePageMetaProps) {
  const pageImage = image || system.metaData.imageUrl;
  const fullUrl = `${system.url}${url}`;
  const fullImageUrl = pageImage.startsWith('http') ? pageImage : `${system.url}${pageImage}`;

  // Service-specific structured data
  const serviceStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": title,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": system.company_name,
      "url": system.url,
      "logo": `${system.url}/logo/devkrest-logo/logo-devkrest-rounded.png`
    },
    "serviceType": serviceType,
    "url": fullUrl,
    "image": fullImageUrl,
    "keywords": keywords.join(', '),
    "areaServed": "Worldwide",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${serviceType} Services`,
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": title
          }
        }
      ]
    }
  };

  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={system.company_name} />
      <meta name="robots" content="index, follow" />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      {/* Hreflang Alternates */}
      <link rel="alternate" hrefLang="en" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content={system.company_name} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:site" content="@devkrest" />
      <meta name="twitter:creator" content="@devkrest" />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(serviceStructuredData)
        }}
      />
    </Head>
  );
}
